import React, { useEffect, useState, useRef } from "react";

const Modal = require("react-modal");

// Set app element only if #root exists (to avoid issues in tests)
const rootElement = document.getElementById("root");
if (rootElement) {
  Modal.setAppElement("#root");
}

interface ImageGalleryProps {
  images: string[][];
  rows: number;
}

export const ImageGallery: React.FC<ImageGalleryProps> = ({ images, rows }) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [imageClass, setImageClass] = useState<string>("");
  const [imageSize, setImageSize] = useState<number | false>(false);

  const galleryRef = useRef<HTMLDivElement>(null);
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);

  const openModal = (image: string) => {
    const hightQualityImg = image.replace("thumbnail_", "gq_");
    setSelectedImage(hightQualityImg);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setSelectedImage(null);
    setModalIsOpen(false);
    setImageClass("");
  };

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    if (naturalWidth > naturalHeight) {
      setImageClass("modal-image landscape");
    } else {
      setImageClass("modal-image portrait");
    }
    e.currentTarget.classList.add("loaded");
  };

  useEffect(() => {
    const updateImageSize = () => {
      if (containerRef !== null && containerRef !== undefined) {
        const gaps = 6;
        const newSize = Math.floor((containerRef.clientHeight - gaps) / rows);
        setImageSize(newSize);
      }
    };

    updateImageSize();
    window.addEventListener("resize", updateImageSize);

    return () => {
      window.removeEventListener("resize", updateImageSize);
    };
  }, [rows, containerRef]);

  useEffect(() => {
    const useVerticalScroll = (event: WheelEvent) => {
      if (event.deltaY !== 0) {
        event.preventDefault();
        if (galleryRef.current) {
          galleryRef.current.scrollLeft += event.deltaY * 2; // Increase scroll speed
        }
      }
    };

    document.addEventListener("wheel", useVerticalScroll, { passive: false });
    return () => {
      document.removeEventListener("wheel", useVerticalScroll);
    };
  }, []);

  // Helper for rendering image thumbnails
  const renderImageThumbnail = (
    image: string,
    colorIndex: number,
    imageIndex: number
  ) => (
    <div
      key={imageIndex}
      className="thumbnail-container"
      style={{ width: imageSize || 0, height: imageSize || 0 }}
    >
      <img
        src={image}
        alt={`flower ${colorIndex}-${imageIndex}`}
        className="thumbnail"
        style={{ width: imageSize || 0, height: imageSize || 0 }}
        onClick={() => openModal(image)}
        onLoad={handleImageLoad}
      />
    </div>
  );

  // Helper for rendering color groups
  const renderColorGroup = (colorGroup: string[], colorIndex: number) => (
    <div key={colorIndex} className="color-group">
      {colorGroup.map((image, imageIndex) =>
        renderImageThumbnail(image, colorIndex, imageIndex)
      )}
    </div>
  );

  return (
    <div
      className="page-container-gallery"
      ref={setContainerRef as React.RefCallback<HTMLDivElement>}
    >
      <div className="horizontal-gallery-container">
        <div className="horizontal-gallery" ref={galleryRef}>
          {images.map(renderColorGroup)}
          <Modal
            isOpen={modalIsOpen}
            className="modal"
            overlayClassName="overlay"
            shouldCloseOnOverlayClick={true}
            onRequestClose={closeModal}
            shouldCloseOnEsc={true}
          >
            {selectedImage && (
              <img
                src={selectedImage}
                alt="Selected"
                className={imageClass}
                onLoad={handleImageLoad}
                onClick={closeModal}
              />
            )}
          </Modal>
        </div>
      </div>
    </div>
  );
};
